import type { components } from "resources/api-schema";

export const PAYMENT_REQUEST_DOCUMENT_ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.ms-excel",
  "image/jpeg",
  "image/png",
  "text/csv",
] as const;

export const DEFAULT_INVOICE_DATE_STRING = "0001-01-01T00:00:00Z";
export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export type PaymentRequestReason =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestReason"];

export const PAYMENT_REQUEST_REASONS: ReadonlyArray<PaymentRequestReason> = [
  "Over",
  "Missing",
  "Other",
  "Short",
  "UnableToApply",
];

export type PaymentRequestRequestedAmountType =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestRequestedAmountType"];

export const PAYMENT_REQUEST_REQUESTED_AMOUNT_TYPES: ReadonlyArray<PaymentRequestRequestedAmountType> =
  ["Repair", "Rental", "TowBill", "Supplement", "Other"];

export type PaymentRequestStatus =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestStatus"];

export const PAYMENT_REQUEST_STATUSES: ReadonlyArray<PaymentRequestStatus> = [
  "New",
  "Open",
  "Refused",
  "PendingShop",
  "PendingInsurer",
  "Closed",
  "PendingHO",
  "TransferredHigherLevel",
] as const;

export type PaymentRequestClosureStatus =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestClosureStatus"];

export const PAYMENT_REQUEST_CLOSURE_STATUSES: ReadonlyArray<PaymentRequestClosureStatus> =
  [
    "NotClosed",
    "AlreadyPaid",
    "AlreadySubmitted",
    "InsurerWontPay",
    "NoAnswerFromShop",
    "InTransit",
    "Unresolved",
    "Resolved",
    "Unknown",
    "Other",
  ] as const;

export type PaymentRequestValidationStatus =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestValidationStatus"];

export type PaymentRequestDocumentType =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestDocumentType"];

export const PAYMENT_REQUEST_DOCUMENT_TYPES: ReadonlyArray<PaymentRequestDocumentType> =
  ["Estimate", "Invoice", "Approval", "Other"] as const;

export type PaymentRequestCommentSource =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestCommentSource"];

export const PAYMENT_REQUEST_COMMENT_SOURCES: ReadonlyArray<PaymentRequestCommentSource> =
  ["HO", "Shop"];

export type PaymentRequestCommentDto =
  components["schemas"]["Fixhub.PaymentRequests.Comment.PaymentRequestCommentDto"];

export type PaymentRequestStatusDto =
  components["schemas"]["Fixhub.PaymentRequests.Status.PaymentRequestStatusDto"];

export type PaymentRequestDocumentDto =
  components["schemas"]["Fixhub.PaymentRequests.Document.PaymentRequestDocumentDto"];

export type PaymentRequestCreateDto =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestCreateDto"];

export type PaymentRequestDto =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestDto"];

export type PaymentRequestUpdateDto =
  components["schemas"]["Fixhub.PaymentRequests.PaymentRequestUpdateDto"];

export type PaymentRequestFormValues = {
  applicantName: string;
  applicantEmail: string;
  adjusterTelephone: string;
  adjusterName: string;
  adjusterEmail: string;
  policyNumber: string;
  fileClaimNumber: string;
  requestReason: PaymentRequestReason | "";
  requestReasonOtherDescription: string;

  /** Format: date-time */
  requestedOn: Date | null;
  /** Format: uuid */
  insurerId: string;
  /** Format: uuid */
  shopId: string;
  /** Format: uuid */
  userId: string;
  /** Format: uuid */
  clerkId: string;
  insuredName: string;
  invoiceCopyRequested: boolean;

  // CURRENCY FIELDS
  totalOriginalAmountInCents: number;
  requestedAmountInCents: number;
  status: PaymentRequestStatus;
  amountDueFromInsurerInCents: number;
  totalBreakdownRepairInCents: number;
  totalBreakdownRentalInCents: number;
  totalBreakdownTowBillInCents: number;
  totalBreakdownSupplementInCents: number;
  totalBreakdownOtherInCents: number;
  deductibleAmountInCents: number;
  hstAmountInCents: number;
  otherCustomerAmountInCents: number;

  totalBreakdownOtherDesc: string;
  requestedAmountTypeOtherDesc: string;
  requestedAmountType: PaymentRequestRequestedAmountType | "";
  comment: string;
  /** Format: date-time */
  invoiceDate: Date | null;
  brandType: components["schemas"]["Fixnetwork.SharedEntities.BrandTypes.BrandClassification"];
  supplierPhoneNumber: string;
  partOrderNumber: string;
  invoiceNumber: string;
  vin: string;
  fleetCardInfoNumber: string;
  originalInvoiceContactEmail: string;
  authorizationNumber: string;
  documents: PaymentRequestDocumentDto[];
};

export type PrivateNoteDto =
  components["schemas"]["Fixhub.PaymentRequests.PrivateNote.PaymentRequestPrivateNoteDto"];

export type PrivateNoteCreateDto =
  components["schemas"]["Fixhub.PaymentRequests.PrivateNote.CreateUpdatePaymentRequestPrivateNoteDto"];
